import { supabase } from '../lib/supabase';

export type NotificationType = 
  | 'new_load'
  | 'new_dispatch'
  | 'dispatch_status_change'
  | 'driver_sms_message'
  | 'new_driver_added'
  | 'new_truck_added'
  | 'new_trailer_added'
  | 'document_uploaded'
  | 'maintenance_due'
  | 'maintenance_completed'
  | 'inspection_due'
  | 'accident_reported'
  | 'load_ready_billing'
  | 'invoice_created'
  | 'payment_received'
  | 'driver_available'
  | 'driver_unavailable';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
  icon?: string;
  priority: NotificationPriority;
  is_read: boolean;
  created_at: string;
  read_at?: string;
}

export interface CreateNotificationParams {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
  icon?: string;
  priority?: NotificationPriority;
}

/**
 * Create a new notification for a user.
 *
 * Handles three possible backend states so this never throws noisily in prod:
 *  1. An existing `create_notification(p_message, p_title, p_type, p_user_id)`
 *     SECURITY DEFINER RPC (the 4-arg signature Postgres hints at). This is the
 *     preferred path because RLS on `notifications` may block direct INSERTs.
 *  2. Direct insert with the full payload (full new-schema deployment).
 *  3. Direct insert with only the core columns if the schema cache rejects the
 *     optional ones (old schema without link/icon/priority).
 *
 * If all three fail we log and return {success:false}; notifications are
 * non-critical so we never throw to the caller.
 */
export async function createNotification(params: CreateNotificationParams) {
  const core = {
    user_id: params.userId,
    type: params.type,
    title: params.title,
    message: params.message,
    is_read: false,
  };
  const fullPayload: Record<string, any> = { ...core };
  if (params.link) fullPayload.link = params.link;
  if (params.icon) fullPayload.icon = params.icon;
  if (params.priority) fullPayload.priority = params.priority;

  // Path 1 — SECURITY DEFINER RPC (bypasses RLS).
  try {
    const { data, error } = await supabase.rpc('create_notification', {
      p_user_id: params.userId,
      p_type: params.type,
      p_title: params.title,
      p_message: params.message,
    });
    if (!error) return { success: true, notificationId: data };
  } catch {
    // swallow — try direct insert next
  }

  // Path 2 — direct insert with full payload.
  try {
    const { data, error } = await supabase
      .from('notifications')
      .insert(fullPayload)
      .select('id')
      .single();
    if (!error) return { success: true, notificationId: data?.id };

    // Path 3 — retry with only the core columns if an optional one was rejected.
    if ((error as any).code === 'PGRST204') {
      const retry = await supabase
        .from('notifications')
        .insert(core)
        .select('id')
        .single();
      if (!retry.error) return { success: true, notificationId: retry.data?.id };
      throw retry.error;
    }
    throw error;
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error };
  }
}

/**
 * Get all notifications for the current user
 */
export async function getUserNotifications(userId: string, limit = 50) {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return { success: true, notifications: data as Notification[] };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return { success: false, notifications: [], error };
  }
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(userId: string) {
  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) throw error;
    return { success: true, count: count || 0 };
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return { success: false, count: 0, error };
  }
}

/**
 * Mark notification as read
 */
export async function markAsRead(notificationId: string) {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ 
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('id', notificationId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { success: false, error };
  }
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(userId: string) {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ 
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error marking all as read:', error);
    return { success: false, error };
  }
}

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId: string) {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting notification:', error);
    return { success: false, error };
  }
}

/**
 * Subscribe to real-time notifications
 */
export function subscribeToNotifications(
  userId: string,
  callback: (notification: Notification) => void
) {
  const channel = supabase
    .channel('notifications')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback(payload.new as Notification);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// Helper functions to create specific notification types

export async function notifyNewLoad(params: {
  userId: string;
  loadNumber: string;
  customerName: string;
  pickupLocation: string;
  deliveryLocation: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'new_load',
    title: 'New Load Created',
    message: `Load ${params.loadNumber} created for ${params.customerName}. ${params.pickupLocation} → ${params.deliveryLocation}`,
    link: `/loads/${params.loadNumber}`,
    icon: 'Package',
    priority: 'normal'
  });
}

export async function notifyNewDispatch(params: {
  userId: string;
  dispatchId: string;
  dispatchNumber: string;
  loadNumber: string;
  driverName: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'new_dispatch',
    title: 'New Dispatch Created',
    message: `Dispatch ${params.dispatchNumber} created for Load ${params.loadNumber}. Driver: ${params.driverName}`,
    link: `/dispatches/${params.dispatchId}`,
    icon: 'Truck',
    priority: 'high'
  });
}

export async function notifyDispatchStatusChange(params: {
  userId: string;
  dispatchId: string;
  dispatchNumber: string;
  status: string;
  driverName: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'dispatch_status_change',
    title: 'Dispatch Status Updated',
    message: `Dispatch ${params.dispatchNumber} (${params.driverName}) is now ${params.status}`,
    link: `/dispatches/${params.dispatchId}`,
    icon: 'Truck',
    priority: 'normal'
  });
}

export async function notifyDriverSMSMessage(params: {
  userId: string;
  driverName: string;
  message: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'driver_sms_message',
    title: `SMS from ${params.driverName}`,
    message: params.message,
    link: '/dispatches',
    icon: 'MessageSquare',
    priority: 'high'
  });
}

export async function notifyNewDriverAdded(params: {
  userId: string;
  driverName: string;
  driverType: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'new_driver_added',
    title: 'New Driver Added',
    message: `${params.driverName} has been added as ${params.driverType}`,
    link: '/drivers',
    icon: 'Users',
    priority: 'normal'
  });
}

export async function notifyNewTruckAdded(params: {
  userId: string;
  truckNumber: string;
  makeModel: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'new_truck_added',
    title: 'New Truck Added',
    message: `Truck ${params.truckNumber} (${params.makeModel}) has been added to the fleet`,
    link: '/trucks',
    icon: 'Truck',
    priority: 'normal'
  });
}

export async function notifyNewTrailerAdded(params: {
  userId: string;
  trailerNumber: string;
  trailerType: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'new_trailer_added',
    title: 'New Trailer Added',
    message: `Trailer ${params.trailerNumber} (${params.trailerType}) has been added to the fleet`,
    link: '/trailers',
    icon: 'Truck',
    priority: 'normal'
  });
}

export async function notifyDocumentUploaded(params: {
  userId: string;
  documentType: string;
  loadNumber: string;
  uploadedBy: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'document_uploaded',
    title: 'Document Uploaded',
    message: `${params.documentType} uploaded for Load ${params.loadNumber} by ${params.uploadedBy}`,
    link: `/loads/${params.loadNumber}`,
    icon: 'FileText',
    priority: 'normal'
  });
}

export async function notifyMaintenanceDue(params: {
  userId: string;
  vehicleNumber: string;
  maintenanceType: string;
  dueDate: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'maintenance_due',
    title: 'Maintenance Due',
    message: `${params.maintenanceType} due for ${params.vehicleNumber} on ${params.dueDate}`,
    link: '/maintenance',
    icon: 'Wrench',
    priority: 'high'
  });
}

export async function notifyMaintenanceCompleted(params: {
  userId: string;
  vehicleNumber: string;
  maintenanceType: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'maintenance_completed',
    title: 'Maintenance Completed',
    message: `${params.maintenanceType} completed for ${params.vehicleNumber}`,
    link: '/maintenance',
    icon: 'Wrench',
    priority: 'normal'
  });
}

export async function notifyInspectionDue(params: {
  userId: string;
  vehicleNumber: string;
  inspectionType: string;
  dueDate: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'inspection_due',
    title: 'Inspection Due',
    message: `${params.inspectionType} due for ${params.vehicleNumber} on ${params.dueDate}`,
    link: '/inspections',
    icon: 'Shield',
    priority: 'high'
  });
}

export async function notifyAccidentReported(params: {
  userId: string;
  driverName: string;
  vehicleNumber: string;
  location: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'accident_reported',
    title: 'Accident Reported',
    message: `Accident reported involving ${params.driverName} in ${params.vehicleNumber} at ${params.location}`,
    link: '/accidents',
    icon: 'AlertTriangle',
    priority: 'urgent'
  });
}

export async function notifyLoadReadyBilling(params: {
  userId: string;
  loadNumber: string;
  customerName: string;
  amount: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'load_ready_billing',
    title: 'Load Ready for Billing',
    message: `Load ${params.loadNumber} (${params.customerName}) is ready to invoice - $${params.amount}`,
    link: '/accounting/ready-billing',
    icon: 'DollarSign',
    priority: 'high'
  });
}

export async function notifyInvoiceCreated(params: {
  userId: string;
  invoiceNumber: string;
  customerName: string;
  amount: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'invoice_created',
    title: 'Invoice Created',
    message: `Invoice ${params.invoiceNumber} created for ${params.customerName} - $${params.amount}`,
    link: '/accounting/billed',
    icon: 'DollarSign',
    priority: 'normal'
  });
}

export async function notifyPaymentReceived(params: {
  userId: string;
  invoiceNumber: string;
  customerName: string;
  amount: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'payment_received',
    title: 'Payment Received',
    message: `Payment received for Invoice ${params.invoiceNumber} from ${params.customerName} - $${params.amount}`,
    link: '/accounting/paid',
    icon: 'DollarSign',
    priority: 'normal'
  });
}

export async function notifyDriverAvailable(params: {
  userId: string;
  driverName: string;
  location: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'driver_available',
    title: 'Driver Available',
    message: `${params.driverName} is now available in ${params.location}`,
    link: '/drivers',
    icon: 'Users',
    priority: 'normal'
  });
}

export async function notifyDriverUnavailable(params: {
  userId: string;
  driverName: string;
  reason: string;
}) {
  return createNotification({
    userId: params.userId,
    type: 'driver_unavailable',
    title: 'Driver Unavailable',
    message: `${params.driverName} is now unavailable - ${params.reason}`,
    link: '/drivers',
    icon: 'Users',
    priority: 'normal'
  });
}
